import { Button, Select } from 'antd'
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { getCookie } from 'cookies-next'
import shuffle from 'lodash/shuffle'
import { useMount } from 'react-use'
import { disassembleHangul } from 'es-hangul'

import { poppins } from '@/components/common/fonts'
import {
  IconPrevArrow,
  IconNextArrow,
  IconShuffle,
} from '@/components/common/Icons'
import { getEnglishPriority } from '@/hooks/useFilterVoices'
import { getLanguage } from '@/lib/text-utils'
import { GENDER, Gender } from '@/constants/gender'
import { VoiceCloneAsset } from '@/types/models'
import { isSameLanguage, isSimilarChineseLanguage } from '@/lib/language-code-utils'

import { CreateVoiceCard } from './CreateVoiceCard'
import { VOICE_TYPE, VoiceCard } from './LegacyVoiceCard'

export const VoiceSelector: FC<{
  myVoices?: VoiceCloneAsset[]
  originalVoice?: {
    modelId: string
    voiceId: string
    language: string
    gender: Gender
    modelName: string
    sampleUrl: string
  }
  voices?: Tts[]

  initialLanguage?: string
  initialGender?: Gender

  voiceId?: string

  onSelectVoice: (voiceId: string) => void

  onClickCreateVoice?: () => void
  onDeleteVoiceClone?: (voiceId: string) => void

  className?: string
}> = ({
  myVoices,
  originalVoice,
  voices,

  initialLanguage,
  initialGender,

  voiceId,

  onSelectVoice,

  onClickCreateVoice,
  onDeleteVoiceClone,

  className,
}) => {
  const { t } = useTranslation('common')

  const locale = (getCookie('NEXT_LOCALE') as string) || 'en'

  const languageOptions = useMemo(() => {
    if (!voices) {
      return []
    }

    const intlLanguage = new Intl.DisplayNames([locale], { type: 'language' })

    return voices
      .map((voice) => {
        const languageCode = voice.language.split('-')[0]

        if (isSimilarChineseLanguage(languageCode)) {
          return 'zh'
        }

        return languageCode
      })
      .filter((value, index, self) => self.indexOf(value) === index)
      .map((code) => {
        let label = code

        /**
         * 잘못된 언어 코드가 들어왔을 경우 대비 (ex: mora)
         */
        try {
          label = intlLanguage.of(code) ?? code
        }
        catch (e) {
          label = code
        }

        return {
          label,
          value: code,
        }
      })
      .sort(({ label: a }, { label: b }) => a.localeCompare(b))
  }, [voices, locale])

  const genderOptions = useMemo(() => {
    return [
      {
        label: t('All'),
        value: 'ALL',
      },
      {
        label: t('Male'),
        value: GENDER.MALE,
      },
      {
        label: t('Female'),
        value: GENDER.FEMALE,
      },
    ]
  }, [t])

  const [languageFilter, setLanguageFilter] = useState<string>(() => {
    if (initialLanguage === 'multi') {
      return locale
    }

    const languageCode = initialLanguage?.split('-')[0] || locale

    if (isSimilarChineseLanguage(languageCode)) {
      return 'zh'
    }

    return languageCode
  })
  const [genderFilter, setGenderFilter] = useState<'ALL' | Gender>(
    initialGender || 'ALL',
  )

  const existsCreatedMyVoices = useMemo(() => {
    return (myVoices?.filter((voice) => !voice.isSample)?.length ?? 0) > 0
  }, [myVoices])

  const otherVoices = useMemo(() => {
    return voices
      ?.map((v) => {
        const foundLocale =
          Object.keys(v.text_intl).find((item) =>
            isSameLanguage(item, locale),
          ) || 'en'

        return {
          ...v,
          title: getLanguage(v.language, locale),
          description: v.text_intl[foundLocale],
        }
      })
      .sort((a, b) => {
        const aEnglishPriorityValue = getEnglishPriority(a.language)
        const bEnglishPriorityValue = getEnglishPriority(b.language)

        const aGenderValue =
          a.gender?.toLocaleUpperCase() === initialGender ? 0 : 1
        const bGenderValue =
          b.gender?.toLocaleUpperCase() === initialGender ? 0 : 1

        return (
          aEnglishPriorityValue - bEnglishPriorityValue ||
          (a.language?.localeCompare(b.language) ?? 0) ||
          aGenderValue - bGenderValue ||
          0
        )
      })
  }, [voices, initialGender, locale])

  const [randomVoices, setRandomVoices] = useState<typeof otherVoices>([])

  const filteredOtherVoices = useMemo(() => {
    return otherVoices?.filter((voice) => {
      if (
        genderFilter !== 'ALL' &&
        voice.gender?.toUpperCase() !== genderFilter
      ) {
        return false
      }

      if (!!randomVoices?.find((randomVoice) => randomVoice.id === voice.id)) {
        return false
      }

      return isSameLanguage(voice.language, languageFilter)
    })
  }, [otherVoices, languageFilter, genderFilter, randomVoices])

  const shuffleRandomVoices = useCallback(() => {
    if (!otherVoices) {
      setRandomVoices([])

      return
    }

    const pickedVoices = shuffle(
      otherVoices.filter(
        (voice) =>
          voice.gender?.toUpperCase() === initialGender &&
          isSameLanguage(
            voice.language,
            initialLanguage === 'multi' || !initialLanguage
              ? locale
              : initialLanguage,
          ),
      ),
    ).slice(0, 2)

    setRandomVoices(pickedVoices)
  }, [otherVoices, initialLanguage, initialGender, locale])

  /**
   * 초기값 세팅
   */
  useMount(() => {
    shuffleRandomVoices()
  })

  /**
   * 초기 선택된 카드로 스크롤
   */
  const selectedCardRef = useRef<HTMLDivElement>()

  useMount(() => {
    selectedCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    })
  })

  return (
    <VoiceSelectorContainer className={className}>
      <Title>{t('Select AI Voice')}</Title>

      <Content>
        {!!myVoices && (
          <Section>
            <SectionHeader>
              <SectionTitle>{t('My Voices')}</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <HorizontalScrollArea>
                {/* 
                내가 만든 음성이 없다면 보이스 클로닝 생성 버튼을 보여준다.
              */}
                {!existsCreatedMyVoices && (
                  <CreateVoiceCard onClick={onClickCreateVoice} />
                )}

                {myVoices?.map((voice) => (
                  <VoiceCard
                    key={voice.id}
                    type={VOICE_TYPE.VOICE_CLONE}
                    title={voice.text_intl[locale] || voice.text_intl.en}
                    gender={voice.gender as Gender}
                    voiceId={voice.id}
                    sampleUrl={voice.resource_url}
                    isSelected={voice.id === voiceId}
                    isSample={voice.isSample}
                    onClick={() => onSelectVoice(voice.id)}
                    onDeleteVoiceClone={onDeleteVoiceClone}
                    ref={(ref) => {
                      if (!!ref && voice.id === voiceId) {
                        selectedCardRef.current = ref
                      }
                    }}
                  />
                ))}
              </HorizontalScrollArea>
            </SectionContent>
          </Section>
        )}

        <Section>
          <SectionHeader>
            <SectionTitle>{t('Recommended AI Voice')}</SectionTitle>
            <SectionControls>
              <RandomButton onClick={() => shuffleRandomVoices()}>
                <IconShuffle /> {t('Random')}
              </RandomButton>
            </SectionControls>
          </SectionHeader>
          <SectionContent>
            <CardGrid>
              {!!originalVoice && (
                <VoiceCard
                  type={VOICE_TYPE.ORIGINAL}
                  language={originalVoice.language}
                  modelName={originalVoice.modelName}
                  title={getLanguage(originalVoice.language, locale)}
                  voiceId={originalVoice.voiceId}
                  sampleUrl={originalVoice.sampleUrl}
                  isSelected={originalVoice.voiceId === voiceId}
                  onClick={() => onSelectVoice(originalVoice.voiceId)}
                  ref={(ref) => {
                    if (!!ref && originalVoice.voiceId === voiceId) {
                      selectedCardRef.current = ref
                    }
                  }}
                />
              )}

              {randomVoices?.map((voice) => (
                <VoiceCard
                  key={voice.id}
                  type={VOICE_TYPE.TTS}
                  title={voice.title}
                  language={voice.language}
                  description={voice.description}
                  gender={(voice.gender as Gender) ?? GENDER.MALE}
                  voiceId={voice.id}
                  sampleUrl={voice.resource_url}
                  isSelected={voice.id === voiceId}
                  onClick={() => onSelectVoice(voice.id)}
                  ref={(ref) => {
                    if (!!ref && voice.id === voiceId) {
                      selectedCardRef.current = ref
                    }
                  }}
                />
              ))}
            </CardGrid>
          </SectionContent>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>{t('Other AI Voice')}</SectionTitle>
            <SectionControls>
              <Filter
                options={languageOptions}
                value={languageFilter}
                onChange={(value) => setLanguageFilter(value as string)}
                showSearch
                filterOption={(input, option) => {
                  if (!option) {
                    return false
                  }

                  if (typeof option.label !== 'string') {
                    return false
                  }

                  const label = disassembleHangul(option.label.toLowerCase())
                  const search = disassembleHangul(input.trim().toLowerCase())

                  return label.includes(search)
                }}
              />
              <Filter
                options={genderOptions}
                value={genderFilter}
                onChange={(value) => setGenderFilter(value as Gender | 'ALL')}
              />
            </SectionControls>
          </SectionHeader>
          <SectionContent>
            {!filteredOtherVoices?.length && (
              <NoResult>{t('No result.')}</NoResult>
            )}

            {!!filteredOtherVoices?.length && (
              <CardGrid>
                {filteredOtherVoices?.map((voice) => (
                  <VoiceCard
                    key={voice.id}
                    type={VOICE_TYPE.TTS}
                    title={voice.title}
                    language={voice.language}
                    description={voice.description}
                    gender={(voice.gender as Gender) ?? GENDER.MALE}
                    voiceId={voice.id}
                    sampleUrl={voice.resource_url}
                    isSelected={voice.id === voiceId}
                    onClick={() => onSelectVoice(voice.id)}
                    ref={(ref) => {
                      if (!!ref && voice.id === voiceId) {
                        selectedCardRef.current = ref
                      }
                    }}
                  />
                ))}
              </CardGrid>
            )}
          </SectionContent>
        </Section>
      </Content>
    </VoiceSelectorContainer>
  )
}

const VoiceSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  height: 100%;
`

const Title = styled.div`
  font-family: ${poppins.string};
  font-size: 16px;
  font-weight: 600;
  line-height: 28px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  padding: 20px 0;

  overflow: auto;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  position: sticky;

  width: 100%;
  top: -20px;
  z-index: 1;

  background-color: #ffffff;
`

const SectionTitle = styled.div`
  font-family: ${poppins.string};
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const SectionControls = styled.div`
  display: flex;
  gap: 12px;
`

const SectionContent = styled.div``

const RandomButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;

  padding: 4px 6px;

  font-family: ${poppins.string};
  font-size: 11px;
  font-weight: 400;
  line-height: 16.5px;
`

const Filter = styled(Select)`
  width: 124px;

  font-family: ${poppins.string};
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;

  &&& .ant-select-selector {
    cursor: pointer;
  }
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 11px;
`

const HorizontalScrollArea: FC<{
  children?: ReactNode
  className?: string
}> = ({ children, className }) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false)
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false)

  useEffect(() => {
    const handleScroll = () => {
      const target = scrollRef.current

      if (!target) {
        return
      }

      setCanScrollLeft(target.scrollLeft > 0)
      setCanScrollRight(
        target.scrollLeft < target.scrollWidth - target.clientWidth,
      )
    }

    const scrollEl = scrollRef.current

    if (!!scrollEl) {
      scrollEl.addEventListener('scroll', handleScroll)
      handleScroll()
    }

    return () => {
      if (!!scrollEl) {
        scrollEl.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  const handleScrollLeft = () => {
    const scrollEl = scrollRef.current

    if (!!scrollEl) {
      scrollEl.scrollTo({
        left: scrollEl.scrollLeft - 200,
        behavior: 'smooth',
      })
    }
  }

  const handleScrollRight = () => {
    const scrollEl = scrollRef.current

    if (!!scrollEl) {
      scrollEl.scrollTo({
        left: scrollEl.scrollLeft + 200,
        behavior: 'smooth',
      })
    }
  }

  return (
    <HorizontalScrollAreaContainer className={className}>
      <ScrollableArea ref={scrollRef}>{children}</ScrollableArea>

      {canScrollLeft && (
        <ScrollButton className="left" onClick={handleScrollLeft}>
          <IconPrevArrow />
        </ScrollButton>
      )}

      {canScrollRight && (
        <ScrollButton className="right" onClick={handleScrollRight}>
          <IconNextArrow />
        </ScrollButton>
      )}
    </HorizontalScrollAreaContainer>
  )
}

const HorizontalScrollAreaContainer = styled.div`
  position: relative;
`

const ScrollableArea = styled.div`
  display: flex;
  gap: 11px;

  padding-bottom: 5px;

  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;

  & > * {
    flex: 0 0 max(calc((100% - (11px * 2)) / 3), 280px);
  }
`

/**
 * &.left
 * &.right
 */
const ScrollButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;

  position: absolute;

  width: 36px;
  height: 36px;
  top: 50%;
  padding: 0;
  border: 0;
  box-shadow: 0px 2px 5px 0px #0000001a;

  border-radius: 9999px;

  transform: translateY(-50%);

  &.left {
    left: 1px;
  }

  &.right {
    right: 1px;
  }
`

const NoResult = styled.div`
  align-self: stretch;

  padding: 16px;

  font-family: ${poppins.string};
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  color: #222222;
`
